// Backend trace-review findings, read from the analytics projection and laid
// on the lanes' turn axis.
//
// The trace-review pipeline judges every conversation as it grows and
// publishes one row per actionable finding to ClickHouse
// (conversation_review_findings). This module is the dashboard's read of that
// table for ONE conversation, shaped into the incident vocabulary the lanes
// review strip and the verdict ledger already speak, so the pipeline's
// findings render through the same marks, tones and summary line as a
// browser-side judge run -- one surface, two sources.
//
// Placement is by MESSAGE ID first, quote text second. A finding carries the
// verbatim quotes its judge cited and, in parallel, the id of the message each
// quote was read from; a quote lands on the loaded turn carrying that message.
// The text fallback (fold case and whitespace, first turn carrying the text)
// serves only rows without ids. It is a fallback and not the rule because a
// fragment shared by many commands -- one dev password across a fortnight of
// diagnostics -- would otherwise pile every finding onto the first turn that
// happens to contain it. A cited message the loaded turns do not carry leaves
// its quote unplaced for the same reason: the record loads its newest page, so
// on a live session most such messages are earlier, and a text fallback pinned
// all of them onto the first loaded turn with the same words. A quote nowhere
// in the loaded turns leaves its finding uncited -- counted in the strip as
// "not located", never marked, because a mark on the axis promises something
// to read at that turn.
import { runQueryRows, rangeToTimeRange } from "./analyticsClient.js";
import { locateNeedleStrings } from "./traceNeedleLens.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { reviewSummaryLine } from "./traceReview.js";
import { LANDING_PAGE_LIMIT } from "./traceTurnFocus.js";

// Findings one read returns. The worst staging trace carried ~400 rows in a
// window; a record read further back than one read's findings reach is read
// on past it (readTraceReviewFindings), never shown short of its messages.
export const FINDINGS_LIMIT = 500;
// Reads one conversation's findings may take: as many as a link's landing
// reads pages of messages, so the findings keep up with the messages however
// far back they are read.
export const FINDINGS_READ_LIMIT = 1 + LANDING_PAGE_LIMIT;
// The review completes after the content it judged was captured, so a window
// anchored on old activity opens a little before it. Re-evaluations can land
// any time later, so the window closes past now.
const WINDOW_LEAD_MS = 24 * 3600 * 1000;
const WINDOW_TAIL_MS = 3600 * 1000;
// The widest completed_at window the analytics surface accepts for this
// entity (its Discovery access pattern), less a minute of clock slack.
export const FINDINGS_WINDOW_MAX_MS = 90 * 24 * 3600 * 1000 - 60 * 1000;

const P = "CONVERSATION_REVIEW_FINDING";
const field = (name) => ({ stepAlias: "f", field: `DIMENSION_FIELD_${P}_${name}` });
const dim = (name, alias) => ({ field: field(name), outputAlias: alias });

// The analytics planner names each output column from a fixed pool of 32
// aliases and fails the whole query -- as an opaque 500 -- past that, so the
// projection is read selectively: every column the strip and dock use, and
// nothing they do not. The test pins the count against this ceiling.
export const ANALYTICS_OUTPUT_ALIAS_CAP = 32;

// Every column the strip and dock read, by output alias. Claim reasoning is
// prose derived from conversation content, same tier as the rationale.
const FIELDS = [
    ["ID", "finding_id"], ["REPORT_ID", "report_id"], ["COMPLETED_AT", "completed_at"],
    ["TITLE", "title"], ["RATIONALE", "rationale"],
    ["REASON", "reason"], ["EVIDENCE_STATUS", "evidence_status"], ["VERIFIER_STATUS", "verifier_status"],
    ["BEHAVIOUR_SEVERITY", "behaviour_severity"], ["BEHAVIOUR_CATEGORY", "behaviour_category"], ["BEHAVIOUR_DEFINITION", "behaviour_definition"],
    ["CONTENT_SENSITIVITY", "content_sensitivity"], ["CONTENT_CATEGORY", "content_category"], ["CONTENT_DEFINITION", "content_definition"],
    ["POLICY_STATUS", "policy_status"], ["POLICY_REFERENCE", "policy_reference"], ["POLICY_NOTE", "policy_note"],
    ["POLICY_CLAIM_RESULT", "policy_claim_result"], ["POLICY_CLAIM_REASONING", "policy_claim_reasoning"],
    ["NORM_STATUS", "norm_status"], ["NORM_REFERENCE", "norm_reference"], ["NORM_NOTE", "norm_note"],
    ["INTENT_STATUS", "intent_status"], ["INTENT_REFERENCE", "intent_reference"], ["INTENT_NOTE", "intent_note"],
    ["INTENT_CLAIM_RESULT", "intent_claim_result"], ["INTENT_CLAIM_REASONING", "intent_claim_reasoning"],
    ["CITATION_QUOTES", "citation_quotes"], ["CITATION_MESSAGE_IDS", "citation_message_ids"],
    ["PROVIDER_MODEL_IDENTIFIER", "model"],
];

export function findingsQuerySpec({ sessionId, fromMs, toMs, limit = FINDINGS_LIMIT }) {
    return {
        root: { alias: "f", entity: `ENTITY_TYPE_${P}` },
        timeRange: rangeToTimeRange({ from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() }),
        filter: { pred: { field: field("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
        output: { dimensions: FIELDS.map(([name, alias]) => dim(name, alias)) },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_DESC" }],
        // One past the ceiling, so "there were more" is a fact and not a guess.
        limit: limit + 1,
    };
}

// The completed_at window for one conversation: the widest window the surface
// accepts, ending a little past now.
//
// It is not drawn from the conversation's activity. The client has no first
// activity time to draw it from: the session span is built from each run's
// LAST-observed time, so a long or re-captured session puts it near now, and a
// window opened a day before it drops every finding completed earlier. The read is
// filtered to one trace and capped at FINDINGS_LIMIT, so the width costs
// nothing. Only an anchor older than the widest window moves it, back onto
// that activity: a conversation that old was reviewed then.
export function findingsWindow({ sessionFromMs = null, traceTimestamp = null, nowMs = Date.now() } = {}) {
    const toMs = nowMs + WINDOW_TAIL_MS;
    const ts = typeof traceTimestamp === "number" ? traceTimestamp : parseServerTimestamp(traceTimestamp);
    const anchors = [sessionFromMs, ts].filter((t) => Number.isFinite(t));
    const earliest = anchors.length ? Math.min(...anchors) - WINDOW_LEAD_MS : Infinity;
    if (earliest < toMs - FINDINGS_WINDOW_MAX_MS) return { fromMs: earliest, toMs: earliest + FINDINGS_WINDOW_MAX_MS };
    return { fromMs: toMs - FINDINGS_WINDOW_MAX_MS, toMs };
}

// -- cache ------------------------------------------------------------------
// Per conversation, so re-opening a trace or switching readings does not
// re-read the projection. Cleared on identity change: the rows are tenant
// content.
const CACHE = new Map();
export const FINDINGS_CACHE_MAX = 32;
onAuthIdentityChange(() => CACHE.clear());

export function findingsCacheKey(sessionId) {
    return `${getAuthIdentityEpoch()}:${sessionId}`;
}
export function readFindingsCache(sessionId) {
    return CACHE.get(findingsCacheKey(sessionId)) || null;
}
export function writeFindingsCache(sessionId, result) {
    const key = findingsCacheKey(sessionId);
    // A read writes as it goes: only a new conversation makes room.
    if (!CACHE.has(key) && CACHE.size >= FINDINGS_CACHE_MAX) {
        const oldest = CACHE.keys().next().value;
        if (oldest !== undefined) CACHE.delete(oldest);
    }
    CACHE.set(key, result);
}
// Harness and test seam: a pre-seeded answer renders through the cache-hit
// path with no backend.
export function seedTraceReviewFindings(sessionId, rows, { truncated = false } = {}) {
    writeFindingsCache(sessionId, { rows: Array.isArray(rows) ? rows : [], truncated });
}
export function clearTraceReviewFindingsCache() {
    CACHE.clear();
}

// -> { rows, truncated }. Throws on a failed read (denied, unreachable, or an
// analytics surface that predates the entity); the caller decides how quiet to
// be about it.
export async function fetchTraceReviewFindings({ sessionId, fromMs, toMs, signal = null, limit = FINDINGS_LIMIT } = {}) {
    if (!sessionId) return { rows: [], truncated: false };
    const rows = await runQueryRows(findingsQuerySpec({ sessionId, fromMs, toMs, limit }), { signal });
    return { rows: rows.slice(0, limit), truncated: rows.length > limit };
}

// A reader holding a conversation's messages back to `sinceMs` needs its
// findings back to a little before it: a review completes after the content
// it judged was captured. Messages of no known time need only the first read.
const neededFor = (sinceMs) => (Number.isFinite(sinceMs) ? sinceMs - WINDOW_LEAD_MS : Infinity);

// Whether a read of findings (readTraceReviewFindings) reaches as far back as
// a reader's messages, or has read all it may. A read holds every finding
// completed at or after the end of its next window; one with no next window
// (a seed, or a page all of one instant) holds all it can.
export function findingsReachBack(got, sinceMs) {
    if (!got) return false;
    if (!got.next || !((got.reads || 0) < FINDINGS_READ_LIMIT)) return true;
    return got.next.toMs <= neededFor(sinceMs);
}

// The cached read of a conversation's findings, when it reaches as far back
// as the reader's messages.
export function findingsCovering(sessionId, sinceMs) {
    const got = readFindingsCache(sessionId);
    return findingsReachBack(got, sinceMs) ? got : null;
}

// A conversation's findings back as far as a reader holds its messages
// (`sinceMs`, the oldest message's time), newest first. The first read is the
// widest window ending now (findingsWindow), as before; a read cut at the
// limit reads on below its oldest finding (that instant again, deduped, since
// more can share it), and a window read whole gives way to the one before it,
// while the messages reach further back. Each read is kept in the cache as it
// lands, so a reader that comes to earlier messages reads on from there. A
// read that fails past the first leaves what came before it, counted as cut
// short, and the cache keeps where to read on from; an aborted `signal`
// stops the reading where it is. -> { rows, truncated, next, reads }; throws
// only on a failed first read. `fetch` is fetchTraceReviewFindings' shape:
// the readers pass theirs.
export async function readTraceReviewFindings({ sessionId, sinceMs = null, traceTimestamp = null, nowMs = Date.now(), signal = null, fetch = fetchTraceReviewFindings, limit = FINDINGS_LIMIT } = {}) {
    if (!sessionId) return { rows: [], truncated: false, next: null, reads: 0 };
    const need = neededFor(sinceMs);
    let got = readFindingsCache(sessionId) || {
        rows: [], truncated: false, reads: 0,
        next: findingsWindow({ sessionFromMs: Number.isFinite(sinceMs) ? sinceMs : null, traceTimestamp, nowMs }),
    };
    while (got.next && got.reads < FINDINGS_READ_LIMIT && (got.reads === 0 || got.next.toMs > need)) {
        if (signal?.aborted) break;
        const { fromMs, toMs } = got.next;
        let back;
        try {
            back = await fetch({ sessionId, fromMs, toMs, limit, signal });
        } catch (e) {
            if (signal?.aborted) break;
            if (!got.reads) throw e;
            return { ...got, truncated: true };
        }
        const page = Array.isArray(back?.rows) ? back.rows : [];
        const have = new Set(got.rows.map((r) => r?.finding_id).filter(Boolean));
        const rows = got.rows.concat(page.filter((r) => !r?.finding_id || !have.has(r.finding_id)));
        let next;
        if (back?.truncated) {
            const times = page.map((r) => parseServerTimestamp(r?.completed_at)).filter(Number.isFinite);
            const oldest = times.length ? Math.min(...times) : NaN;
            next = Number.isFinite(oldest) && oldest + 1 < toMs ? { fromMs, toMs: oldest + 1 } : null;
        } else {
            next = { fromMs: fromMs - FINDINGS_WINDOW_MAX_MS, toMs: fromMs };
        }
        got = { rows, truncated: !!back?.truncated, next, reads: got.reads + 1 };
        // Two readers of one conversation can read at once: the cache keeps
        // whichever has read further.
        const kept = readFindingsCache(sessionId);
        if (!kept || !((kept.reads || 0) > got.reads)) writeFindingsCache(sessionId, got);
    }
    return got;
}

// -- shaping ----------------------------------------------------------------

const str = (v) => (v == null ? "" : String(v)).trim();
const list = (v) => (Array.isArray(v) ? v.filter((q) => typeof q === "string" && q) : []);

// A frame's claim verdict as the ledger's { holds, reasoning }. An empty
// result means no verdict was recorded -- the frame did not object, or the
// verifier did not run -- which is not "refuted", so it is null here.
function claimOf(result, reasoning) {
    const r = str(result).toLowerCase();
    if (r === "upheld") return { holds: true, reasoning: str(reasoning) || null };
    if (r === "refuted") return { holds: false, reasoning: str(reasoning) || null };
    return null;
}
const frame = (status, ref, note, refKey) => {
    const s = str(status).toLowerCase();
    if (!s) return null;
    return { status: s, [refKey]: str(ref) || null, note: str(note) || null };
};

const NIL_UUID = "00000000-0000-0000-0000-000000000000";
const messageIdOf = (v) => {
    const id = str(v).toLowerCase();
    return id && id !== NIL_UUID ? id : null;
};

// A row's citations as (quote, message id) pairs zipped BEFORE any filtering:
// the projection emits the two arrays index-aligned, and a clipped quote can
// be an empty string while its message id never is, so dropping empties from
// each array on its own would shift every later id onto the wrong quote.
function citationsOf(row) {
    const quotes = Array.isArray(row?.citation_quotes) ? row.citation_quotes : [];
    const ids = Array.isArray(row?.citation_message_ids) ? row.citation_message_ids : [];
    return quotes
        .map((quote, i) => ({ quote: typeof quote === "string" ? quote : "", messageId: messageIdOf(ids[i]) }))
        .filter((c) => c.quote);
}

// Turn indices by the message id each loaded turn carries. A message can span
// more than one turn (a body split into items), so every carrier is kept.
function turnsByMessageId(turnInputs) {
    const map = new Map();
    (turnInputs || []).forEach((t, i) => {
        const id = messageIdOf(t?.messageId);
        if (!id) return;
        if (!map.has(id)) map.set(id, []);
        map.get(id).push(i);
    });
    return map;
}

// Each quote on the turn carrying its cited message; the text search, over
// that turn only, picks the lane cell that should carry a pin. Quotes without a
// resolvable message id fall back to their FIRST occurrence in the loaded
// turns, the ledger's own convention. A quote whose message is known but not
// loaded stays unplaced.
function placeQuotes(cited, turnInputs, byMessage, located) {
    return cited.map((citation) => {
        const { quote, messageId } = citation;
        let turn = null;
        let cells = [];
        const candidates = (messageId && byMessage.get(messageId)) || [];
        if (candidates.length) {
            const scoped = [];
            for (const t of candidates) scoped[t] = turnInputs[t];
            const hit = locateNeedleStrings(scoped, [quote])[0] || null;
            turn = hit?.turns?.[0] ?? candidates[0];
            cells = hit?.cells?.filter((c) => c.turn === turn) || [];
        } else if (!messageId) {
            const hit = located.get(quote) || null;
            turn = hit?.turns?.[0] ?? null;
            cells = turn == null ? [] : (hit?.cells?.filter((c) => c.turn === turn) || []);
        }
        return {
            ...citation,
            quote,
            verified: turn == null ? "missing" : "exact",
            // 1-based, like the ledger's citations: reviewMarks converts once.
            locatedTurn: turn == null ? null : turn + 1,
            cells,
            // The message is known and not on the axis: an earlier page carries it.
            unloaded: !!messageId && !candidates.length,
        };
    });
}

function citedOf(citations) {
    return (citations || [])
        .map((citation) => ({
            ...citation,
            quote: typeof citation?.quote === "string" ? citation.quote : "",
            messageId: messageIdOf(citation?.messageId),
        }))
        .filter((citation) => citation.quote);
}

// A placer over one set of turns, for any number of citations: the message
// index and the text search are built once, not once per finding, and only a
// quote that names no message is looked for over the whole record (the rest
// are looked for on their own message's turns).
function placerOf(turnInputs, cited) {
    const byMessage = turnsByMessageId(turnInputs);
    const loose = [...new Set(cited.filter((citation) => !citation.messageId).map((citation) => citation.quote))];
    const located = new Map(loose.length ? locateNeedleStrings(turnInputs, loose).map((item) => [item.needle, item]) : []);
    return (some) => placeQuotes(some, turnInputs, byMessage, located);
}

// The one placement rule for both analytics rows and saved review documents.
// Callers supply the same turn-input shape used by the Lanes needle locator;
// message ids remain authoritative and text fallback is reserved for
// citations that do not name a message.
export function placeReviewCitations(citations = [], turnInputs = []) {
    const cited = citedOf(citations);
    return placerOf(turnInputs, cited)(cited);
}

// Two reports over overlapping windows can raise the same finding twice
// (re-review, re-evaluation). Same title over the same quotes from the same
// messages is one finding; the newest row speaks for it. The message ids are
// part of the key because the same words in different messages are different
// commands, not one finding found twice.
function dedupeKey(row) {
    const cited = citationsOf(row).map((c) => `${c.messageId || ""}|${c.quote}`).sort().join(" ;; ");
    return `${str(row.title).toLowerCase()} :: ${cited}`;
}

// Rows -> the ledger's report shape: { incidents, withheld: 0, unlocated,
// truncated, completedAt }. `turnInputs` is the lane-hinted text per turn the
// joins census also reads (turnsToJoinInputs), so a quote resolves to a turn
// AND, where a lane claimed the block, to the cell that should carry a pin.
export function buildFindingsReport(rows = [], turnInputs = [], { truncated = false } = {}) {
    const seen = new Map();
    const fresh = [];
    // The ids the dedupe dropped, per surviving row. A re-review raises the
    // same finding under a NEW finding_id, so a link minted from any of them --
    // a notification sent before the re-review -- has to resolve to the row
    // that spoke for it. Without this, a deep link to a re-reviewed finding
    // matches nothing.
    const superseded = [];
    for (const row of rows || []) {
        const key = dedupeKey(row);
        const at = seen.get(key);
        if (at != null) {
            const id = str(row.finding_id);
            if (id) superseded[at].push(id);
            continue;
        }
        seen.set(key, fresh.length);
        fresh.push(row);
        superseded.push([]);
    }
    let completedAt = null;
    const citedRows = fresh.map((row) => citedOf(citationsOf(row)));
    const place = placerOf(turnInputs, citedRows.flat());
    const incidents = fresh.map((row, i) => {
        const citations = place(citedRows[i]);
        const placed = citations.filter((c) => c.verified !== "missing");
        const severity = str(row.behaviour_severity).toLowerCase();
        const sensitivity = str(row.content_sensitivity).toLowerCase();
        const at = parseServerTimestamp(row.completed_at);
        if (Number.isFinite(at) && (completedAt == null || at > completedAt)) completedAt = at;
        return {
            id: str(row.finding_id) || null,
            supersededIds: superseded[i],
            reportId: str(row.report_id) || null,
            completedAt: Number.isFinite(at) ? at : null,
            title: str(row.title) || "(untitled finding)",
            rationale: str(row.rationale) || null,
            reason: str(row.reason).toLowerCase() || null,
            evidenceStatus: str(row.evidence_status).toLowerCase() || null,
            verifierStatus: str(row.verifier_status).toLowerCase() || null,
            behaviour: severity ? { severity, category: str(row.behaviour_category) || null, definition: str(row.behaviour_definition) || null } : null,
            content: sensitivity ? { sensitivity, category: str(row.content_category) || null, definition: str(row.content_definition) || null } : null,
            policy: frame(row.policy_status, row.policy_reference, row.policy_note, "clause"),
            norms: frame(row.norm_status, row.norm_reference, row.norm_note, "norm"),
            intent: frame(row.intent_status, row.intent_reference, row.intent_note, "intent"),
            policyClaim: claimOf(row.policy_claim_result, row.policy_claim_reasoning),
            intentClaim: claimOf(row.intent_claim_result, row.intent_claim_reasoning),
            model: str(row.model) || null,
            citations,
            citedCount: placed.length,
            citationCount: citations.length,
            uncited: placed.length === 0,
            earlier: placed.length === 0 && citations.some((c) => c.unloaded),
            turns: [...new Set(placed.map((c) => c.locatedTurn))].sort((a, b) => a - b),
            cells: placed.flatMap((c) => c.cells),
        };
    });
    return {
        incidents,
        withheld: 0,
        unlocated: incidents.filter((i) => i.uncited).length,
        earlier: incidents.filter((i) => i.earlier).length,
        truncated: !!truncated,
        // Rows read, before the dedupe: what "first N shown" counts.
        read: (rows || []).length,
        completedAt,
        source: "pipeline",
    };
}

// The strip's one-line verdict, in the manual review strip's words plus what
// only a pipeline read can be short of: findings whose messages sit in pages
// not yet read, quotes nowhere in the loaded turns, and rows past the read
// ceiling.
export function findingsSummaryLine(report) {
    if (!report) return "";
    const total = report.incidents?.length || 0;
    const located = (report.incidents || []).filter((i) => !i.uncited).length;
    // Nothing placed is not "clean": the findings exist, the loaded turns just
    // do not carry their quotes, and the line has to say so.
    if (total && !located) {
        const parts = [`${total} finding${total === 1 ? "" : "s"}`, report.earlier === total ? "all in earlier messages" : "none located in the loaded turns"];
        if (report.truncated) parts.push(`first ${report.read || FINDINGS_LIMIT} shown`);
        return parts.join(" \u00b7 ");
    }
    const parts = [reviewSummaryLine(report)];
    const earlier = report.earlier || 0;
    if (earlier) parts.push(`${earlier} in earlier messages`);
    if (report.unlocated > earlier) parts.push(`${report.unlocated - earlier} not located`);
    if (report.truncated) parts.push(`first ${report.read || FINDINGS_LIMIT} shown`);
    return parts.join(" \u00b7 ");
}

// One finding named in the URL, so a notification about it can open the lanes
// reading ON it rather than on a conversation carrying several. It is an
// arrival instruction, not a coordinate the reader writes back: the position a
// finding occupies is decided HERE, by matching its cited quotes against the
// loaded turns, so a sender has nothing but the id to send.
export const FINDING_FOCUS_PARAM = "finding";

const FINDING_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Parse the wire form. Anything that is not a finding id yields null rather
 * than a guess -- the same rule parseTurnFocus states for a stale URL.
 */
export function parseFindingFocus(raw) {
    const text = String(raw ?? "").trim().toLowerCase();
    return FINDING_ID_RE.test(text) ? text : null;
}

/**
 * The incident a finding id names, or null. Also matches an id the dedupe
 * superseded, so a link minted before a re-review still resolves. Ids are
 * compared case-insensitively: the analytics read hands back a canonical
 * hyphenated uuid whose case it does not promise (inventoryRows.js makes the
 * same allowance), and a link that matched nothing over letter case would fail
 * the way this lookup exists to prevent.
 */
export function findIncidentById(report, id) {
    const want = parseFindingFocus(id);
    if (!want) return null;
    for (const inc of report?.incidents || []) {
        if (parseFindingFocus(inc.id) === want) return inc;
        if ((inc.supersededIds || []).some((other) => parseFindingFocus(other) === want)) return inc;
    }
    return null;
}

// Incidents by the 0-based turn they are marked on, for the dock: a click on
// the strip asks "what was found here", over the folded range the mark stands
// for.
export function findingsByTurn(report) {
    const map = new Map();
    for (const inc of report?.incidents || []) {
        if (inc.uncited) continue;
        for (const t of inc.turns) {
            const turn = t - 1;
            if (!map.has(turn)) map.set(turn, []);
            map.get(turn).push(inc);
        }
    }
    return map;
}

export function findingsInRange(byTurn, turn, through = turn) {
    if (!byTurn) return [];
    const out = [];
    const seen = new Set();
    for (let t = turn; t <= Math.max(turn, through); t++) {
        for (const inc of byTurn.get(t) || []) {
            const key = inc.id || inc;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(inc);
        }
    }
    return out;
}

// Cell pins: one per (lane, turn) a located quote landed in, carrying the worst
// tone of the findings that share the cell.
export function findingCellPins(report, toneOf) {
    const byCell = new Map();
    const rank = { danger: 2, warn: 1, calm: 0 };
    for (const inc of report?.incidents || []) {
        if (inc.uncited) continue;
        const tone = toneOf(inc);
        for (const c of inc.cells || []) {
            const key = `${c.lane} ${c.turn}`;
            const cur = byCell.get(key);
            if (!cur) byCell.set(key, { lane: c.lane, turn: c.turn, tone, count: 1, title: inc.title });
            else {
                cur.count += 1;
                if ((rank[tone] ?? 0) > (rank[cur.tone] ?? 0)) { cur.tone = tone; cur.title = inc.title; }
            }
        }
    }
    return [...byCell.values()];
}
